import {
  Alert,
  Box,
  Card,
  CardContent,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  useGetAllPostsQuery,
  useGetPostsByOrgQuery,
} from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { useAuthUser } from "hooks/useAuthUser";
import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import dayjs from "dayjs";
import { AdminRoutes } from "config/routes";
import { ROLES } from "types/user";
import { PostAuthor } from "pages/Post/PostAuthor";

export const Discussion: FC = () => {
  const { user } = useAuthUser();
  const isAdmin = user?.role === ROLES.ADMIN;
  const organization = useAppSelector(organizationSelectedSelector);
  const orgId = organization?.org_id;

  const orgPosts = useGetPostsByOrgQuery({
    variables: { orgId: orgId || "", limit: 3 },
    skip: isAdmin || !orgId,
    fetchPolicy: "cache-and-network",
  });

  const allPosts = useGetAllPostsQuery({
    variables: { limit: 3 },
    skip: !isAdmin,
    fetchPolicy: "cache-and-network",
  });

  const { data, loading, error } = isAdmin ? allPosts : orgPosts;

  const posts = data?.post || [];

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            Vet Discussions
          </Typography>
          <Link
            component={RouterLink}
            to={AdminRoutes.Posts}
            underline="hover"
            sx={{ textDecoration: "none" }}
          >
            VIEW ALL
          </Link>
        </Box>

        {!isAdmin && !orgId && (
          <Alert severity="info">Select an organization to see discussions.</Alert>
        )}
        {error && (
          <Alert severity="error">Could not load discussion posts.</Alert>
        )}
        {(isAdmin || orgId) && loading && !data && (
          <Typography variant="body2">Loading...</Typography>
        )}
        {(isAdmin || orgId) && !loading && !error && posts.length === 0 && (
          <Alert severity="info">No recent discussion posts</Alert>
        )}

        <Stack spacing={1.5} mt={1}>
          {posts.map((post) => (
            <Box
              key={post.post_id}
              component={RouterLink}
              to={`/posts/${post.post_id}`}
              sx={{
                display: "block",
                textDecoration: "none",
                color: "inherit",
                borderRadius: 1,
                px: 1,
                py: 0.75,
                mx: -1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Box mb={0.75}>
                <PostAuthor
                  name={post.author?.name}
                  pictureUrl={post.author?.picture_url}
                  equippedStoreItems={
                    (post.author as any)?.equipped_store_items
                  }
                  size={28}
                  nameVariant="caption"
                />
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary"
              >
                {post.title}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="caption" color="text.secondary">
                  {dayjs(post.created_at).format("MMM D, YYYY")} ·
                </Typography>
                <ChatBubbleOutlineIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {post.answers_aggregate?.aggregate?.count || 0}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
